import { z } from 'zod';
import { Cache, getApiInstance, harden, Response } from '../core';
import { JsonValue } from '../../Utility/JsonValue';

export const SchedulePartValidator = z.object({
    runStartTime: z.string(),
    runEndTime: z.string(),
    runDays: z.string(),
});
export type SchedulePart = z.infer<typeof SchedulePartValidator>;

export const PlaylistItemBaseValidator = z.object({
    groupId: z.string().nullable(),
    announcementId: z.string(),
    dateStart: z.string().nullable(),
    dateEnd: z.string().nullable(),
    annStatus: z.enum(['active', 'inactive']),
    goFullScreen: z.boolean(),
    annSeconds: z.number(),
    announcementOrder: z.number(),
    updatedTimestamp: z.number().nullable(),
    scheduleParts: z.array(SchedulePartValidator).nonempty(),
    announcementName: z.string(),
    mediaLibraryItemId: z.string().optional(),
    template: z.string(),
    type: z.string(),
    backgroundColor: z.string().optional().default(''),
});
export type PlaylistItemBase = z.infer<typeof PlaylistItemBaseValidator>;

export const AnnouncementItemCoreValidator = z.object({
    x: z.number(),
    y: z.number(),
    z: z.number().nullable(),
    animation: z.string().nullable(),
    width: z.number(),
    height: z.number(),
});
export type AnnouncementItemCore = z.infer<typeof AnnouncementItemCoreValidator>;

export const AnnouncementImageValidator = AnnouncementItemCoreValidator.extend({
    url: z.string(),
});
export type AnnouncementImage = z.infer<typeof AnnouncementImageValidator>;

export const AnnouncementTextboxValidator = AnnouncementItemCoreValidator.extend({
    id: z.string(),
    text: z.string(),
});
export type AnnouncementTextbox = z.infer<typeof AnnouncementTextboxValidator>;

export const AnnouncementShapeValidator = AnnouncementItemCoreValidator.extend({
    type: z.string(),
    color: z.string(),
    shaperadius: z.string().optional(),
});
export type AnnouncementShape = z.infer<typeof AnnouncementShapeValidator>;

export const AnnouncementAssetValidator = PlaylistItemBaseValidator.extend({
    width: z.number(),
    height: z.number(),
    bgimg: z.string().optional(),
    images: z.array(AnnouncementImageValidator),
    textboxes: z.array(AnnouncementTextboxValidator),
    shapes: z.array(AnnouncementShapeValidator),
});
export type AnnouncementAsset = z.infer<typeof AnnouncementAssetValidator>;

export const ImageAssetValidator = PlaylistItemBaseValidator.extend({
    width: z.number(),
    height: z.number(),
    bgimg: z.string(),
    type: z.literal('image'),
    images: z.array(AnnouncementImageValidator),
});
export type ImageAsset = z.infer<typeof ImageAssetValidator>;

export const VideoBaseValidator = PlaylistItemBaseValidator.extend({
    videoTotalDuration: z.number().int(),
});
export type VideoBase = z.infer<typeof VideoBaseValidator>;

export const ReachVideoAssetValidator = VideoBaseValidator.extend({
    width: z.number(),
    height: z.number(),
    videoId: z.string(),
    videoStartTime: z.number(),
    type: z.literal('video'),
});
export type ReachVideoAsset = z.infer<typeof ReachVideoAssetValidator>;

export const YoutubeAssetValidator = VideoBaseValidator.extend({
    youtubeLink: z.string(),
    videoStartTime: z.number(),
    enableClosedCaptioning: z.boolean(),
});
export type YoutubeAsset = z.infer<typeof YoutubeAssetValidator>;

export const VimeoAssetValidator = VideoBaseValidator.extend({
    vimeoVideoId: z.string(),
    type: z.literal('vimeo'),
    videoStartTime: z.number(),
    enableClosedCaptioning: z.boolean(),
});
export type VimeoAsset = z.infer<typeof VimeoAssetValidator>;

export const PlaylistItemValidator = z.union([
    AnnouncementAssetValidator,
    ImageAssetValidator,
    ReachVideoAssetValidator,
    YoutubeAssetValidator,
    VimeoAssetValidator,
]);

export type PlaylistItem = z.infer<typeof PlaylistItemValidator>;

export const getPlaylistResponseSchema = z.object({
    announcements: PlaylistItemValidator.array().optional(),
    hash: z.string(),
    errors: z.array(z.any()),
});

export type GetPlaylistResponse = z.infer<typeof getPlaylistResponseSchema>;

export async function getPlaylist(params: {
    playlistId: string;
    lastQueryHash: string;
}): Response<GetPlaylistResponse> {
    const { api, cacheConfig } = await getApiInstance();
    const urlPath = `playlist/${params.playlistId}`;

    cacheConfig(Cache.GetAndUpdate, urlPath, {
        excludeParams: ['lastQueryHash'],
        update: (oldData, newData) => {
            const oldDataHash = getHashValue(oldData);
            const newDataHash = getHashValue(newData);
            if (newDataHash !== oldDataHash) {
                return newData;
            } else {
                return oldData;
            }
        },
    });

    const response = await api.get(urlPath, {
        params: {
            lastQueryHash: params.lastQueryHash,
        },
    });

    return harden(getPlaylistResponseSchema, response.data);
}

function getHashValue(playlistData: JsonValue): JsonValue | null {
    if (playlistData === null || typeof playlistData !== 'object') {
        return null;
    }
    if (!('hash' in playlistData)) {
        return null;
    }

    const hashValue = playlistData.hash;
    return hashValue;
}
